/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { format, isValid, parseISO } from 'date-fns';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { io, Socket } from 'socket.io-client';

import { IoSend } from 'react-icons/io5';
import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';
import { useAuth } from '~/hooks/Auth';
import { useHelpNotifications } from '~/hooks/HelpNotifications';

import { Container, Filter, Modal } from './styles';
import { KrClose, KrEye } from '~/components/KoroIcons';
import FiltersLists from '~/components/FiltersLists';
import Textarea from '~/components/Textarea';

import Toast from '~/utils/toast';
import ChatMessageBubble from './ChatMessageBubble';
import { IOption } from '~/components/Select';
import InfiniteScroll from '~/components/InfiniteScroll';
import Loading from '~/components/Loading';
import { useResize } from '~/hooks/Resize';

interface IChat {
  id: string;
  content: string;
  role: string;
  date: string;
  read?: boolean;
  readAt?: string | null;
}

interface IDetailGroup {
  title: string;
  items: { label: string; value: string }[];
}

interface IRequest {
  details: IDetailGroup[];
  updateAt: string;
  situation: string;
  id: number;
  name: string;
  date: string;
  hour: string;
  status: string;
  profile: string;
  type: string;
  subject: string;
  chatId: string;
  event: string;
  organizer: string;
  chat: IChat[];
}

interface IMessage {
  _id: string;
  conversation_id: string;
  role: string;
  content: string;
  created_at: string;
  read?: boolean;
  read_at?: string | null;
}

interface IRequestData {
  updated_at: string;
  situation: string;
  id: number;
  chat_id: string;
  nivel: number;
  subject: string;
  for_administrator: boolean;
  status: string;
  created_at: string;
  messages: IMessage[];
  description?: string | null;
  ticket_id?: number | null;
  user?: {
    id?: number;
    name?: string;
    email?: string | null;
    additionalInformation?: {
      document?: string | null;
      phone?: string | null;
      birthdate?: string | null;
      gender?: string | null;
    } | null;
  } | null;
  event?: {
    id?: number;
    name?: string;
    code?: string | null;
    date?: string | null;
    hour?: string | null;
    end_date?: string | null;
    end_hour?: string | null;
    sales_end_date?: string | null;
    free?: boolean | null;
  } | null;
  organizer?: {
    id?: number;
    company_id?: number | null;
    name?: string;
  } | null;
  order?: {
    id?: number;
    code?: string | null;
    name?: string | null;
    email?: string | null;
    status?: string | null;
    total?: string | number | null;
    service_fee?: string | number | null;
    processing_fee?: string | number | null;
    discount?: string | number | null;
    installments?: number | null;
    quantity_total?: number | null;
    quantity_cancelled?: number | null;
    type_cancellation?: string | null;
    created_at?: string | null;
  } | null;
  typeIncident: {
    type: string;
  };
}

interface IRequestResponse {
  data: IRequestData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IFilter {
  type?: string;
  status?: string;
  organizerId?: string;
  eventId?: string;
}

interface IOrganizer {
  id: number;
  name: string;
}

const formatEventDate = (date?: string | null, hour?: string | null) => {
  if (!date) return undefined;
  const parsed = parseISO(date);
  if (!isValid(parsed)) return undefined;
  const formatted = format(parsed, 'dd/MM/yyyy');
  return hour ? `${formatted} às ${hour.slice(0, 5)}` : formatted;
};

const formatDateTime = (date?: string | null) => {
  if (!date) return undefined;
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : undefined;
};

const genderLabels: Record<string, string> = {
  M: 'Masculino',
  F: 'Feminino',
};

const booleanLabel = (value?: boolean | null) => {
  if (typeof value !== 'boolean') return undefined;
  return value ? 'Sim' : 'Não';
};

const formatCurrency = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return undefined;
  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const buildDetails = (incident: IRequestData): IDetailGroup[] => {
  const groups: {
    title: string;
    items: { label: string; value?: string | number | null }[];
  }[] = [
    {
      title: 'Usuário',
      items: [
        { label: 'ID', value: incident.user?.id },
        { label: 'Nome', value: incident.user?.name },
        { label: 'E-mail', value: incident.user?.email },
        {
          label: 'CPF/Documento',
          value: incident.user?.additionalInformation?.document,
        },
        {
          label: 'Telefone',
          value: incident.user?.additionalInformation?.phone,
        },
        {
          label: 'Data de nascimento',
          value: formatEventDate(
            incident.user?.additionalInformation?.birthdate
          ),
        },
        {
          label: 'Gênero',
          value: incident.user?.additionalInformation?.gender
            ? genderLabels[incident.user.additionalInformation.gender] ||
              incident.user.additionalInformation.gender
            : undefined,
        },
      ],
    },
    {
      title: 'Organizador',
      items: [
        { label: 'ID', value: incident.organizer?.id },
        { label: 'Nome', value: incident.organizer?.name },
        { label: 'ID da empresa', value: incident.organizer?.company_id },
      ],
    },
    {
      title: 'Evento',
      items: [
        { label: 'ID', value: incident.event?.id },
        { label: 'Nome', value: incident.event?.name },
        { label: 'Código', value: incident.event?.code },
        {
          label: 'Início',
          value: formatEventDate(incident.event?.date, incident.event?.hour),
        },
        {
          label: 'Término',
          value: formatEventDate(
            incident.event?.end_date,
            incident.event?.end_hour
          ),
        },
        {
          label: 'Fim das vendas',
          value: formatDateTime(incident.event?.sales_end_date),
        },
        { label: 'Gratuito', value: booleanLabel(incident.event?.free) },
      ],
    },
    {
      title: 'Pedido',
      items: [
        { label: 'ID', value: incident.order?.id },
        { label: 'Código', value: incident.order?.code },
        { label: 'Status', value: incident.order?.status },
        { label: 'Total', value: formatCurrency(incident.order?.total) },
        {
          label: 'Taxa de serviço',
          value: formatCurrency(incident.order?.service_fee),
        },
        {
          label: 'Taxa de processamento',
          value: formatCurrency(incident.order?.processing_fee),
        },
        {
          label: 'Desconto',
          value: formatCurrency(incident.order?.discount),
        },
        { label: 'Parcelas', value: incident.order?.installments },
        {
          label: 'Quantidade total',
          value: incident.order?.quantity_total,
        },
        {
          label: 'Quantidade cancelada',
          value: incident.order?.quantity_cancelled,
        },
        {
          label: 'Tipo de cancelamento',
          value: incident.order?.type_cancellation,
        },
        {
          label: 'Data do pedido',
          value: formatDateTime(incident.order?.created_at),
        },
      ],
    },
    {
      title: 'Chamado',
      items: [
        { label: 'Inscrição', value: incident.ticket_id },
        { label: 'Descrição', value: incident.description },
      ],
    },
  ];

  return groups
    .map((group) => ({
      title: group.title,
      items: group.items
        .filter(
          (item) =>
            item.value !== undefined &&
            item.value !== null &&
            String(item.value).trim() !== ''
        )
        .map((item) => ({ label: item.label, value: String(item.value) })),
    }))
    .filter((group) => group.items.length > 0);
};

const getReadStatus = (message: IChat): 'sent' | 'read' | undefined => {
  if (message.role !== 'admin' || typeof message.read !== 'boolean') {
    return undefined;
  }
  return message.read ? 'read' : 'sent';
};

const Help: React.FC = () => {
  const { user } = useAuth();
  const { width } = useResize();
  const {
    unreadChats,
    unreadByChat,
    hasUnread,
    markConversationRead,
    setOpenChat,
  } = useHelpNotifications();
  const formRef = useRef<FormHandles>(null);
  const chatDivRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket>();
  const skipScrollResetRef = useRef(false);
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [requestSel, setRequestSel] = useState({} as IRequest);
  const [requests, setRequests] = useState<IRequest[]>([]);
  const [answer, setAnswer] = useState('');
  const [filters, setFilter] = useState({} as IFilter);
  const [chatMessages, setChatMessages] = useState<IChat[]>([]);
  const [chatPage, setChatPage] = useState(1);
  const [chatTotalPages, setChatTotalPages] = useState(0);
  const [loadingMoreChat, setLoadingMoreChat] = useState(false);
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalRequests, setTotalRequests] = useState(0);

  useEffect(() => {
    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  useEffect(() => {
    if (skipScrollResetRef.current) {
      skipScrollResetRef.current = false;
      return;
    }
    if (chatDivRef.current) {
      chatDivRef.current.scrollTop = 0;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (show && requestSel.chatId) {
      setOpenChat(requestSel.chatId);
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        markConversationRead(requestSel.chatId);
      }
    } else {
      setOpenChat(null);
    }
    return () => setOpenChat(null);
  }, [show, requestSel.chatId, setOpenChat, markConversationRead]);

  // Socket.IO: conectar na room ao abrir chat, desconectar ao fechar
  useEffect(() => {
    if (!requestSel.chatId) return undefined;

    const socket = io(process.env.REACT_APP_API_URL || '');
    socketRef.current = socket;

    socket.emit('incident:join', requestSel.chatId);

    socket.on(
      'incident:message',
      (msg: {
        _id?: string;
        content?: string;
        message?: string;
        role?: string;
        user_id?: number;
        created_at?: string;
        read?: boolean;
        read_at?: string | null;
      }) => {
        const messageDate = msg.created_at
          ? parseISO(msg.created_at)
          : new Date();
        const formattedMessageDate = format(messageDate, 'dd/MM/yyyy HH:mm');

        const newMsg: IChat = {
          id: msg._id || `${Date.now()}`,
          content: (msg.content || msg.message) as string,
          role: msg.role || 'user',
          date: formattedMessageDate,
          read: msg.read,
          readAt: msg.read_at,
        };

        if (
          newMsg.role === 'user' &&
          document.visibilityState === 'visible' &&
          document.hasFocus()
        ) {
          markConversationRead(requestSel.chatId);
        }

        setChatMessages((prev) => {
          if (prev.some((message) => message.id === newMsg.id)) return prev;
          return [newMsg, ...prev];
        });

        setRequestSel((prev) => ({
          ...prev,
          updateAt: formattedMessageDate,
        }));
        setRequests((prev) =>
          prev.map((request) =>
            request.chatId === requestSel.chatId
              ? { ...request, updateAt: formattedMessageDate }
              : request
          )
        );
      }
    );

    socket.on(
      'incident:read',
      (data: {
        conversation_id: string;
        reader: string;
        message_ids: string[];
        read_at: string;
      }) => {
        if (data.reader !== 'user') return;
        setChatMessages((prev) =>
          prev.map((message) =>
            data.message_ids.includes(message.id)
              ? { ...message, read: true, readAt: data.read_at }
              : message
          )
        );
      }
    );

    return () => {
      socket.emit('incident:leave', requestSel.chatId);
      socket.disconnect();
      socketRef.current = undefined;
    };
  }, [requestSel.chatId, user.id, markConversationRead]);

  const handleLoadRequests = useCallback(
    async (
      pageData: number,
      type?: string,
      status?: string,
      eventId?: string,
      organizerId?: string
    ) => {
      try {
        setLoadingMore(true);
        const response = await api.get<IRequestResponse>('incidents', {
          params: {
            page: pageData,
            type,
            status,
            eventId,
            organizerId,
          },
        });

        const data = response.data.data.map<IRequest>((incident) => {
          const createdAt = parseISO(incident.created_at);
          let profile = '';
          switch (incident.nivel) {
            case 1:
              profile = 'Atleta';
              break;
            case 2:
              profile = 'Organizador';
              break;
            default:
              profile = 'N/A';
          }
          return {
            details: buildDetails(incident),
            id: incident.id,
            name: incident.user?.name || 'N/A',
            date: format(createdAt, 'dd/MM/yyyy'),
            hour: format(createdAt, 'HH:mm'),
            status: incident.status,
            subject: incident.subject,
            profile,
            type: incident.typeIncident.type,
            situation:
              incident.status === 'Fechado' || incident.status === 'Resolvido'
                ? 'Fechado'
                : 'Aberto',
            updateAt: incident.messages[0]?.created_at
              ? format(
                  parseISO(incident.messages[0]?.created_at),
                  'dd/MM/yyyy HH:mm'
                )
              : 'N/A',
            chatId: incident.chat_id,
            organizer: incident.organizer?.name || 'N/A',
            event: incident.event?.name || 'N/A',
            chat: incident.messages.map<IChat>((message) => {
              return {
                id: message._id,
                content: message.content,
                role: message.role,
                date: format(parseISO(message.created_at), 'dd/MM/yyyy'),
              };
            }),
          };
        });

        setTotalRequests(response.data.total);

        if (pageData === 1) {
          console.log({ data });
          setRequests(data);
        } else {
          setRequests((oldState) => [...oldState, ...data]);
        }
        setTotalPages(response.data.pages);
      } catch (error) {
        console.error(error);
        setLoadingMore(false);
      } finally {
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    handleLoadRequests(1);
  }, [handleLoadRequests]);

  const handleClose = useCallback(() => {
    setRequestSel({} as IRequest);
    setChatMessages([]);
    setShowDetails(false);
    setShow(false);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          answer: Yup.string().required('A resposta é obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const reply = await api.post<{
          _id: string;
          content: string;
          read?: boolean;
          read_at?: string | null;
          created_at: string;
        }>(`ai/chat/${requestSel.chatId}/reply`, {
          message: data.answer,
        });

        const sent = reply.data;
        if (sent?._id) {
          setChatMessages((prev) =>
            prev.some((message) => message.id === sent._id)
              ? prev
              : [
                  {
                    id: sent._id,
                    content: sent.content,
                    role: 'admin',
                    date: format(parseISO(sent.created_at), 'dd/MM/yyyy HH:mm'),
                    read: sent.read,
                    readAt: sent.read_at,
                  },
                  ...prev,
                ]
          );
        }

        setAnswer('');
        formRef.current?.reset();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          console.log(errors);
          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Erro ao enviar mensagem!');
        }
      }
    },
    [requestSel.chatId]
  );

  const handleSubmitFilter = useCallback(
    (data) => {
      console.log({ data });
      const type = data.type !== 'todos' ? data.type : undefined;
      const status =
        data.situationHelp !== 'todos-abertos' ? data.situationHelp : undefined;

      const eventId = data.event !== 'Todos' ? data.eventId : undefined;
      const organizerId =
        data.organizer !== 'todos' ? data.organizer : undefined;
      setFilter({
        type,
        status,
        eventId,
        organizerId,
      });
      handleLoadRequests(1, type, status, eventId, organizerId);
      setPage(1);
    },
    [handleLoadRequests]
  );

  const handleOpenChat = useCallback(
    async (request) => {
      setShow(true);
      setRequestSel(request);

      try {
        // Carrega histórico de mensagens via API do chat
        if (request.chatId) {
          const chatResponse = await api.get(`ai/chat/${request.chatId}`, {
            params: { page: 1 },
          });
          const messagesData = chatResponse.data.data || chatResponse.data;
          const msgs = (
            Array.isArray(messagesData) ? messagesData : []
          ).map<IChat>((message: IMessage) => ({
            id: message._id || `${Date.now()}-${Math.random()}`,
            content: message.content,
            role: message.role,
            date: format(parseISO(message.created_at), 'dd/MM/yyyy HH:mm'),
            read: message.read,
            readAt: message.read_at,
          }));
          setChatMessages(msgs);
          setChatPage(1);
          setChatTotalPages(chatResponse.data.pages || 0);
        } else {
          // Fallback: usa mensagens já carregadas da listagem
          setChatMessages(request.chat);
          setChatPage(1);
          setChatTotalPages(0);
        }

        if (request.status === 'Novo' || request.status === 'Reaberto') {
          await api.patch(`incidents/admins/change-status/${request.id}`, {
            status: 'Em análise',
          });

          const requestIndex = requests.findIndex(
            (requestData) => requestData.id === request.id
          );

          if (requestIndex >= 0) {
            requests[requestIndex].status = 'Em análise';
          }

          setRequests(requests);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [requests]
  );

  const handleLoadMoreChatMessages = useCallback(async () => {
    if (!requestSel.chatId || loadingMoreChat) return;
    if (chatPage >= chatTotalPages) return;

    try {
      setLoadingMoreChat(true);
      const nextPage = chatPage + 1;
      const chatResponse = await api.get(`ai/chat/${requestSel.chatId}`, {
        params: { page: nextPage },
      });
      const messagesData = chatResponse.data.data || chatResponse.data;
      const olderMsgs = (
        Array.isArray(messagesData) ? messagesData : []
      ).map<IChat>((message: IMessage) => ({
        id: message._id || `${Date.now()}-${Math.random()}`,
        content: message.content,
        role: message.role,
        date: format(parseISO(message.created_at), 'dd/MM/yyyy HH:mm'),
        read: message.read,
        readAt: message.read_at,
      }));

      setChatMessages((prev) => {
        const existingIds = new Set(prev.map((message) => message.id));
        const uniqueOlderMsgs = olderMsgs.filter(
          (message) => !existingIds.has(message.id)
        );
        skipScrollResetRef.current = true;
        return [...prev, ...uniqueOlderMsgs];
      });
      setChatPage(nextPage);
      setChatTotalPages(chatResponse.data.pages || chatTotalPages);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingMoreChat(false);
    }
  }, [requestSel.chatId, chatPage, chatTotalPages, loadingMoreChat]);

  const handleChangeAnswer = useCallback((e) => {
    setAnswer(e.target.value);
  }, []);

  const handleChatScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      // .height-chat usa flex-direction: column-reverse, então o "topo" visual
      // (mensagens mais antigas) corresponde ao extremo do scroll oposto ao
      // scrollTop = 0. Dependendo do navegador, scrollTop pode ser positivo
      // ou negativo ao rolar para cima, então tratamos os dois casos.
      const distanceFromTop = scrollHeight - clientHeight + scrollTop;
      const distanceFromTopNegative =
        scrollHeight - clientHeight - Math.abs(scrollTop);
      const threshold = 60;

      const isNearTop =
        distanceFromTop <= threshold || distanceFromTopNegative <= threshold;

      if (isNearTop) {
        handleLoadMoreChatMessages();
      }
    },
    [handleLoadMoreChatMessages]
  );

  console.log(requestSel);

  const handleClickFinalizeRequest = useCallback(() => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: 'Finalizar chamado?',
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then(async (e) => {
      if (e.isConfirmed) {
        await api.patch(`incidents/admins/change-status/${requestSel.id}`, {
          status: requestSel.subject === 'Geral' ? 'Encerrado' : 'Resolvido',
        });

        const requestIndex = requests.findIndex(
          (requestData) => requestData.id === requestSel.id
        );

        if (requestIndex >= 0) {
          requests[requestIndex].status =
            requestSel.subject === 'Geral' ? 'Encerrado' : 'Resolvido';
          requests[requestIndex].situation = 'Fechado';
        }

        setRequests(requests);

        handleClose();

        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Chamado finalizado com sucesso!',
        });
      }
    });
  }, [requestSel, requests, handleClose]);

  const handleLoad = useCallback(async () => {
    if (!loadingMore) {
      if (page < totalPages) {
        await handleLoadRequests(
          page + 1,
          filters.type,
          filters.status,
          filters.eventId,
          filters.organizerId
        );
        setPage(page + 1);
      } else {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, page, totalPages, handleLoadRequests, filters]);

  return (
    <Container>
      <>
        <InfiniteScroll
          id="infinite-scroll"
          className="infinite-scroll py-3"
          direction="vertical"
          onInfiniteLoad={handleLoad}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="zoom col-12 px-0">
                <div className="bg-white d-flex justify-content-between align-items-center p-3 p-lg-4">
                  <h1 className="mb-0 d-flex align-items-center">
                    Chamados
                    {hasUnread && <span className="unread-dot" />}
                  </h1>
                </div>
              </div>
              <div className="zoom col-12 px-0">
                <div className="bg-white p-3 mt-3">
                  <Filter className="col-12 px-0 mt-lg-3">
                    {console.log(filters)}
                    <FiltersLists
                      showBtnClean={width > 991}
                      onSubmitFilters={handleSubmitFilter}
                      organizer={width > 991}
                      organizersData={organizers}
                      event
                      type
                      types={[
                        {
                          id: 1,
                          value: 'Cancelamento',
                          selected: filters.type === '1',
                        },
                        {
                          id: 2,
                          value: 'Troca de tamanho do kit',
                          selected: filters.type === '2',
                        },
                        {
                          id: 3,
                          value: 'Troca de modalidade/categoria',
                          selected: filters.type === '3',
                        },
                        {
                          id: 4,
                          value: 'Transferência de inscrição',
                          selected: filters.type === '4',
                        },
                        {
                          id: 5,
                          value: 'Outros',
                          selected: filters.type === '5',
                        },
                      ]}
                      situationHelp
                      situations={[
                        {
                          id: 'Em análise',
                          value: 'Em análise',
                          selected: filters.status === 'Em análise',
                        },
                        {
                          id: 'Fechado',
                          value: 'Fechado',
                          selected: filters.status === 'Fechado',
                        },
                        {
                          id: 'Nova interação',
                          value: 'Nova interação',
                          selected: filters.status === 'Nova interação',
                        },
                        {
                          id: 'Novo',
                          value: 'Novo',
                          selected: filters.status === 'Novo',
                        },
                        {
                          id: 'Reaberto',
                          value: 'Reaberto',
                          selected: filters.status === 'Reaberto',
                        },
                        {
                          id: 'Resolvido',
                          value: 'Resolvido',
                          selected: filters.status === 'Resolvido',
                        },
                      ]}
                    />
                  </Filter>

                  <div className="d-lg-flex mt-4 justify-content-between align-items-center">
                    <h3 className="mb-0 h5 h4-lg fw-semibold">
                      Usuários que solicitaram ajuda
                    </h3>{' '}
                    <p className="h6 mb-0 text-black text-end">
                      Total: {totalRequests}
                    </p>
                  </div>
                  <hr className="mt-3 mb-0" />

                  {requests.map((request) => (
                    <div
                      key={request.id}
                      className={`d-flex justify-content-between align-items-center border-bottom pb-2 pt-3 ${
                        unreadChats.includes(request.chatId) ? 'unread-row' : ''
                      }`}
                    >
                      <div className="w-100 d-lg-flex align-items-center">
                        <div className="w-lg-3 d-none d-lg-block ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            ID
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.id}
                          </span>
                        </div>
                        <div className="w-lg-12 ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            {request.name}
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.date} {request.hour}
                          </span>
                        </div>
                        <div className="w-lg-5 d-none d-lg-block ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Perfil
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.profile}
                          </span>
                        </div>
                        <div className="w-lg-15 ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Tipo
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.type}
                          </span>
                        </div>
                        <div className="w-lg-7 d-none d-lg-block ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Situação
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.situation || 'Aber/Fech'}
                          </span>
                        </div>
                        <div className="w-lg-10 ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Status
                          </span>
                          <span
                            className={`${
                              request.status === 'Novo' && 'pending'
                            } ${
                              (request.status === 'Em análise' ||
                                request.status === 'Nova interação' ||
                                request.status === 'Reaberto') &&
                              'visualized'
                            } ${
                              (request.status === 'Resolvido' ||
                                request.status === 'Fechado') &&
                              'solved'
                            } status d-block`}
                          >
                            {request.status}
                          </span>
                        </div>
                        <div className="w-lg-10 ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Protocolo
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.chatId}
                          </span>
                        </div>
                        <div className="w-lg-10 d-none d-lg-block ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Organizador
                          </span>
                          <span className="h6 small-lg fw-normal d-block date ellipsis">
                            {request.organizer}
                          </span>
                        </div>
                        <div className="w-lg-10 ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Evento
                          </span>
                          <span className="h6 small-lg fw-normal d-block date ellipsis">
                            {request.event}
                          </span>
                        </div>
                        <div className="w-lg-10 ms-lg-2 text-center text-lg-start">
                          <span className="h6 small-lg fw-normal d-block">
                            Atualização
                          </span>
                          <span className="h6 small-lg fw-normal d-block date">
                            {request.updateAt || 'N/A'}
                          </span>
                        </div>

                        <div className="w-lg-3 text-center text-lg-start">
                          <button
                            type="button"
                            onClick={() => handleOpenChat(request)}
                            className=" border-0 rounded-circle bg-orange position-relative"
                          >
                            <KrEye size={16} color="#fff" />
                            {unreadByChat[request.chatId] > 0 && (
                              <span className="unread-badge">
                                {unreadByChat[request.chatId]}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </InfiniteScroll>
        <Modal
          className="modal-filter"
          size="lg"
          centered
          show={show}
          onHide={handleClose}
        >
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
              <Modal.Title className="mb-0 mt-5 w-100">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="h6 mb-0">
                    <span>Protocolo: </span>
                    {requestSel?.chatId}
                  </span>
                  <span
                    className={`d-flex align-items-center ${
                      (requestSel?.status === 'Novo' ||
                        requestSel?.status === 'Reaberto') &&
                      'new'
                    } ${requestSel?.status === 'Em análise' && 'visualized'} ${
                      (requestSel?.status === 'Resolvido' ||
                        requestSel?.status === 'Fechado') &&
                      'solved'
                    } h6 mb-0 fw-normal px-3 py-1`}
                  >
                    {requestSel?.status}
                  </span>
                </div>
                <h2 className="h5 h4-lg my-3">{requestSel?.type}</h2>
                <div className="d-lg-flex align-items-center justify-content-between">
                  <p className="name fw-normal mb-0">
                    {requestSel?.name} | {requestSel?.profile}{' '}
                  </p>
                  <span className="name fw-normal ">
                    Última atualização: {requestSel?.updateAt}
                  </span>
                </div>
              </Modal.Title>
              <button
                type="button"
                onClick={handleClose}
                className="close border-0 bg-transparent"
              >
                <KrClose size={20} color="#989898" />
              </button>
            </Modal.Header>
            <Modal.Body className="pt-0 px-3 px-sm-5">
              <div className="row">
                <div className="col-12">
                  <hr />
                  {requestSel?.details?.length > 0 && (
                    <div className="mb-3">
                      <button
                        type="button"
                        className="btn-filter btn fw-normal py-1 px-3"
                        onClick={() => setShowDetails((prev) => !prev)}
                      >
                        {showDetails ? 'Menos detalhes' : 'Mais detalhes'}
                      </button>
                      {showDetails && (
                        <div className="details-box mt-3">
                          {requestSel.details.map((group) => (
                            <div key={group.title} className="details-group">
                              <h4 className="details-title">{group.title}</h4>
                              {group.items.map((item) => (
                                <p key={item.label} className="details-item">
                                  <strong>{item.label}:</strong> {item.value}
                                </p>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    ref={chatDivRef}
                    className="height-chat pe-2"
                    onScroll={handleChatScroll}
                  >
                    {chatMessages.map((chatSel) => (
                      <ChatMessageBubble
                        key={chatSel.id}
                        content={chatSel.content}
                        isMe={
                          chatSel.role === 'admin' ||
                          chatSel.role === 'assistant'
                        }
                        date={chatSel.date}
                        status={getReadStatus(chatSel)}
                        readAt={chatSel.readAt}
                      />
                    ))}
                    {loadingMoreChat && (
                      <p className="text-center small text-muted mb-2">
                        Carregando mensagens anteriores...
                      </p>
                    )}
                  </div>

                  {requestSel?.status !== 'Resolvido' && (
                    <>
                      <p className="mb-1 mt-3">Resposta:</p>
                      <div className="d-flex align-items-end">
                        <Textarea
                          rows={5}
                          name="answer"
                          className="answer"
                          onChange={handleChangeAnswer}
                          value={answer}
                        />{' '}
                        <button type="submit" className="send ms-3">
                          <IoSend size={24} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-5 px-lg-5">
              {requestSel?.status !== 'Resolvido' && (
                <button
                  type="button"
                  className="me-auto btn-filter d-flex align-items-center justify-content-center btn fw-normal py-2 px-4 mt-2 mb-0"
                  onClick={handleClickFinalizeRequest}
                >
                  Finalizar Chamado
                </button>
              )}
            </Modal.Footer>
          </Form>
        </Modal>
        <Loading active={loadingMore} text="Carregando..." />
      </>
    </Container>
  );
};

export default Help;
